import React from 'react';
import { eventEndInclusiveKey, eventIsAllDay, toDateKey } from '../../tab_calendar/viewServices/calendar_grid_utils';
import { formatDateLocal } from 'shared_items/formatConventions';
import { formatPlannerEventHm, PLANNER_TIME_FORMAT_24 } from '../../moduleServices/planner_time_format';

const EventCard = ({
  event,
  onClick,
  fadePast = true,
  showWeekday = true,
  showHour = false,
  timeFormatPreference = PLANNER_TIME_FORMAT_24,
}) => {
  const endKey = eventEndInclusiveKey(event);
  const isPast = fadePast && endKey && endKey < formatDateLocal(new Date());

  const dateParts = (dateString) => {
    const key = toDateKey(dateString);
    if (!key) return null;
    const [year, month, day] = key.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return {
      dayLabel: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      weekday: date.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 3).toLowerCase(),
    };
  };

  const DateBit = ({ dateString, timeText }) => {
    const parts = dateParts(dateString);
    if (!parts) return null;
    return (
      <span className="inline-flex items-baseline gap-1">
        <span>{parts.dayLabel}</span>
        {showHour && timeText && <span className="italic">{timeText}</span>}
        {showWeekday && (
          <>
            <span aria-hidden>·</span>
            <span className="italic">{parts.weekday}</span>
          </>
        )}
      </span>
    );
  };

  const hm = (iso) => formatPlannerEventHm(iso, timeFormatPreference);

  const formatDateTime = () => {
    const startRaw = event.start_datetime || event.start_date;
    const sameDay = (a, b) => toDateKey(a) && toDateKey(a) === toDateKey(b);

    if (!event.start_datetime && event.start_date) {
      return <DateBit dateString={startRaw} />;
    }

    if (!eventIsAllDay(event) && event.start_datetime) {
      const endIso = event.end_datetime || event.start_datetime;
      if (sameDay(startRaw, endIso)) {
        const t0 = hm(event.start_datetime);
        const t1 = hm(endIso);
        const timePart = t0 && t1 && t0 !== t1 ? `${t0}–${t1}` : t0 || '';
        return <DateBit dateString={startRaw} timeText={timePart} />;
      }
      return (
        <span className="inline-flex items-center gap-1">
          <DateBit dateString={startRaw} timeText={hm(event.start_datetime)} />
          <span>to</span>
          <DateBit dateString={endIso} timeText={hm(endIso)} />
        </span>
      );
    }

    const endRaw =
      event.end_datetime ||
      event.end_date ||
      event.start_datetime ||
      event.start_date;
    if (!dateParts(startRaw)) return null;
    if (sameDay(startRaw, endRaw)) return <DateBit dateString={startRaw} />;
    return (
      <span className="inline-flex items-center gap-1">
        <DateBit dateString={startRaw} />
        <span>to</span>
        <DateBit dateString={endRaw} />
      </span>
    );
  };

  const calendarColor = event.calendar?.color;

  return (
    <div
      onClick={onClick}
      className={`cursor-pointer transition-colors border-l-4 py-1.5 px-2 ${isPast ? 'hover:bg-gray-50/70 opacity-90' : 'hover:bg-gray-50'}`}
      style={{
        borderLeftColor: calendarColor || '#D1D5DB'
      }}
    >
      <div className="flex items-center gap-3">
        <span className={`text-xs whitespace-nowrap flex-shrink-0 ${isPast ? 'text-gray-400' : 'text-gray-500'}`}>
          {formatDateTime()}
        </span>
        <span className={`text-sm font-medium line-clamp-1 ${isPast ? 'text-gray-400' : 'text-gray-800'}`}>
          {event.title || 'Untitled Event'}
        </span>
      </div>
    </div>
  );
};

export default EventCard;
